import React, { useState, useEffect, useRef, useMemo } from 'react';
import AdSlot from '../components/AdSlot';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { createPost, createTestimony, deleteTestimony, getSettings, getUserByUsername, likeTestimony, listFollowing, listSaved, listTestimonies, saveItem, unlikeTestimony, unsaveItem, updateTestimony, viewTestimony } from '../utils/secureFunctions';
import { getMyPhoto } from '../utils/myProfile';
import { deleteMediaObject } from '../utils/mediaApi';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import CommentSection from '../components/CommentsModal';
import { validateContent, filterProfanity } from '../utils/contentModeration';
import { isVideoFile } from '../utils/videoCompression';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { describeModerationError, notifyIfHeldForReview } from '../utils/moderationErrors';
import type { ModerationTokenMap } from '../utils/moderatedUpload';
import MediaBox from '../components/MediaBox';
 
import { trackContentView, trackInteraction } from '../utils/activityTracking';
import ReelEditorV2 from '../components/ReelEditorV2';
import { compressImage } from '../utils/imageCompression';
import { getGlobalMuted, onGlobalMuteChange } from '../utils/audioPrefs';
import MusicLibraryPicker, { MusicTrack } from '../components/MusicLibraryPicker';
import { extractHashtags, extractMentions, renderTextWithLinks } from '../utils/text';
import { createNotification } from '../utils/notificationHelpers';
import SignedAudio from '../components/SignedAudio';
import SignedVideo from '../components/SignedVideo';
import ThumbnailPicker, { uploadThumbnail } from '../components/ThumbnailPicker';
import AmbientBackdrop from '../components/AmbientBackdrop';
import WatchTracker from '../components/WatchTracker';
import UserLink from '../components/UserLink';

// Helper function to get video duration
const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    
    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

interface Testimony {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  title: string;
  content: string;
  imageURL?: string;
  videoURL?: string;
  thumbnailURL?: string | null;
  likeCount: number;
  likedByMe?: boolean;
  comments: number;
  views?: number;
  featured: boolean;
  createdAt: any;
}

const TestimonyWall: React.FC = () => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 1024; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 1024); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [testimonies, setTestimonies] = useState<Testimony[]>([]);
  const [activeTab, setActiveTab] = useState<'forYou' | 'yourTestimonies'>('forYou');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [audience, setAudience] = useState<'public' | 'friends'>('public');
  const [followingUsers, setFollowingUsers] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [editThumb, setEditThumb] = useState<File | null>(null);
  // Single media input (native chooser handles camera/gallery on mobile)
  const mediaInputRef = React.useRef<HTMLInputElement | null>(null);
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPhoto, setUserPhoto] = useState('');
  // Editor + music state for video testimonies
  const [showEditor, setShowEditor] = useState(false);
  // Quick trim removed; use full editor when needed
  const MAX_TESTIMONY_SECONDS = 300; // 5 minutes
  const [bgMusic, setBgMusic] = useState<MusicTrack | null>(null);
  const [showPickMusic, setShowPickMusic] = useState(false);
  const [editorCaption, setEditorCaption] = useState('');
  // Camera recording modal removed; use file input capture instead
  // Saved items (testimonies)
  const [savedIds, setSavedIds] = useState<Record<string, boolean>>({});
  // Background music mute state and refs per testimony
  const [bgMuted, setBgMuted] = useState<Record<string, boolean>>({});
  const bgAudioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const globalMuteRef = useRef<boolean>(getGlobalMuted());
  // Container refs for intersection-based playback control
  const testimonyContainerRefs = useRef<Record<string, HTMLElement | null>>({});



  // Ensure music never auto-plays until its testimony enters viewport; reworked pause logic
  const pauseAllExcept = (keepId?: string) => {
    Object.entries(bgAudioRefs.current).forEach(([k, a]) => {
      if (!a) return;
      if (k === keepId) return; // keep target playing (handled elsewhere)
      try { a.pause(); } catch {}
    });
  };
  const toggleBgMute = (id: string) => {
    setBgMuted((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const el = bgAudioRefs.current[id];
      if (el) {
        el.muted = !!next[id];
        if (!el.muted) {
          // pause other audios then play this one
          pauseAllExcept(id);
          try { el.play?.(); } catch {}
        }
      }
      return next;
    });
  };

  // Auto pause/resume background music based on viewport visibility
  useEffect(() => {
    const pauseAllExcept = (keepId?: string) => {
      Object.entries(bgAudioRefs.current).forEach(([k, a]) => {
        if (a && k !== keepId) {
          try { a.pause(); } catch {}
        }
      });
    };

    const observer = new IntersectionObserver((entries) => {
      // Detect if any testimony video is actively playing; suppress bg audio when true
      const anyVideoPlaying = Array.from(document.querySelectorAll('video')).some(v => !v.paused && v.readyState >= 2);
      entries.forEach((entry) => {
        const el = entry.target as HTMLElement;
        const id = el.dataset.testimonyid;
        if (!id) return;
        const audio = bgAudioRefs.current[id];
        if (!audio) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Play only this audio; others paused
          pauseAllExcept(id);
          if (!bgMuted[id] && !globalMuteRef.current && !anyVideoPlaying) {
            try { audio.play(); } catch {}
          }
        } else {
          try { audio.pause(); audio.currentTime = 0; } catch {}
        }
      });
    }, { threshold: [0, 0.5, 1] });
    Object.entries(testimonyContainerRefs.current).forEach(([id, el]) => { if (el) observer.observe(el); });

    const refsSnapshot = { ...bgAudioRefs.current };

    return () => {
      try { observer.disconnect(); } catch {}
      Object.values(refsSnapshot).forEach((a) => { if (a) try { a.pause(); } catch {} });
    };
  }, [bgMuted]);

  // Respond to global mute changes
  useEffect(() => {
    const off = onGlobalMuteChange((muted) => {
      globalMuteRef.current = muted;
      Object.entries(bgAudioRefs.current).forEach(([id, el]) => {
        if (!el) return;
        if (muted) {
          el.muted = true;
          try { el.pause(); } catch {}
        } else if (!bgMuted[id]) {
          el.muted = false;
          try { el.play(); } catch {}
        }
      });
    });
    return off;
  }, [bgMuted]);
  
  const [commentingTestimony, setCommentingTestimony] = useState<Testimony | null>(null);
  // Edit/Delete state
  const [editingTestimony, setEditingTestimony] = useState<Testimony | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editAudience, setEditAudience] = useState<'public' | 'friends'>('public');
  // Repost state
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostTarget, setRepostTarget] = useState<Testimony | null>(null);
  const [repostQuote, setRepostQuote] = useState('');
  const [postingRepost, setPostingRepost] = useState(false);
  // Video aspect per testimony id (true = tall/portrait → 9:16, false = wide → 16:9)
  const [videoTall, setVideoTall] = useState<Record<string, boolean>>({});
  // Mentions/hashtags suggestions
  const [mentionCandidates, setMentionCandidates] = useState<Array<{ id: string; name: string; username?: string; photoURL?: string }>>([]);
  const [suggestions, setSuggestions] = useState<{ open: boolean; kind: 'mention' | 'hashtag' | null; query: string; start: number }>({ open: false, kind: null, query: '', start: -1 });
  const contentRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Track a testimony view when its card mounts
  const TestimonyViewTracker: React.FC<{ id: string }> = ({ id }) => {
    useEffect(() => {
      trackContentView(id, 'testimonies');
      viewTestimony(id).catch(() => undefined);
    }, [id]);
    return null;
  };
  const viewedOnceRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Load user data
    const loadUserData = async () => {
      if (!auth.currentUser) return;
      try {
        const settings = await getSettings();
        if (settings.settings.defaultAudience === 'friends' || settings.settings.defaultAudience === 'public') {
          setAudience(settings.settings.defaultAudience);
        }
      } catch {}
      setUserName(auth.currentUser.displayName || 'Anonymous');
      setUserPhoto((await getMyPhoto()));
    };

    loadUserData();

    // Load current user's following list (for audience filtering)
    const loadFollowing = async () => {
      if (!auth.currentUser) return;
      try {
        const res = await listFollowing(auth.currentUser.uid, { limit: 500 });
        setFollowingUsers(res.users.map((u) => u.id));
      } catch (e) {
        console.warn('Failed to load following list for audience filter');
      }
    };

    loadFollowing();

    // Load testimonies
    let mounted = true;
    const load = async () => {
      try {
        const mode = activeTab === 'yourTestimonies' ? 'mine' : 'forYou';
        const res = await listTestimonies({ limit: 50, mode });
        if (!mounted) return;
        setTestimonies(res.testimonies as any);
      } catch (e) {
        console.warn('Failed to load testimonies', e);
      }
    };
    load();
    const t = window.setInterval(load, 20000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, [activeTab]);

  // Load mention candidates (people you follow) for suggestions
  useEffect(() => {
    const run = async () => {
      try {
        if (!followingUsers || followingUsers.length === 0) { setMentionCandidates([]); return; }
        const res = await listFollowing(auth.currentUser?.uid || '', { limit: 500 });
        setMentionCandidates(res.users.map((u) => ({ id: u.id, name: u.name || 'Anonymous', username: u.username || '', photoURL: u.photoURL || '' })));
      } catch {
        setMentionCandidates([]);
      }
    };
    run();
  }, [followingUsers]);

  // Subscribe to saved items for testimonies
  useEffect(() => {
    if (!auth.currentUser) return;
    let mounted = true;
    const load = async () => {
      try {
        const res = await listSaved({ contentType: 'testimony', limit: 500 });
        if (!mounted) return;
        const map: Record<string, boolean> = {};
        res.saved.forEach((s) => { map[s.contentId] = true; });
        setSavedIds(map);
      } catch (e) {
        console.warn('Failed to load saved testimonies', e);
      }
    };
    load();
    const t = window.setInterval(load, 20000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, []);

  const handleCreateTestimony = async () => {
    if (!auth.currentUser) return;

    // Validate text fields
    // Enforce media required (photo or video)
    if (!mediaFile) { alert('Please add a photo or video to share your testimony.'); return; }
    // Title/content optional, but validate if present
    if (title.trim()) {
      const vt = validateContent(title);
      if (!vt.isValid) { alert(vt.message); return; }
    }
    if (content.trim()) {
      const vc = validateContent(content);
      if (!vc.isValid) { alert(vc.message); return; }
    }

    setPosting(true);

    try {
      let imageURL = '';
      let videoURL = '';

      let mediaPath = '';
      let moderationTokens: ModerationTokenMap | null = null;
      if (mediaFile) {
        const isImage = mediaFile.type.startsWith('image');
        setIsUploading(true);
        setUploadProgress(0);
        const upload = await uploadModeratedMedia({
          folder: 'testimonies',
          field: isImage ? 'imageURL' : 'videoURL',          file: mediaFile,
          contentType: mediaFile.type || (isImage ? 'image/jpeg' : 'video/mp4'),
          metadata: {
            title: title || '',
            content: content || '',
            audience: audience || 'public',
          },
          onProgress: (pct) => setUploadProgress(pct),
        });
        const url = upload.objectKey;
        mediaPath = upload.storagePath;
        moderationTokens = { [upload.field]: upload.proof };
        if (isImage) imageURL = url;
        else videoURL = url;
      }
      const thumb = videoURL && thumbFile ? await uploadThumbnail(thumbFile) : null;
      if (thumb) moderationTokens = { ...(moderationTokens || {}), thumbnailURL: thumb.proof } as ModerationTokenMap;

      const safeTitle = filterProfanity(title);
      const safeContent = filterProfanity(content);

      // Extract hashtags and mentions
      const hashtags = extractHashtags(`${safeTitle}\n${safeContent}`);
      const mentions = extractMentions(`${safeTitle}\n${safeContent}`);
      const filteredMentions = mentions.filter((m) => m !== 'followers');

      const created = await createTestimony({
        title: safeTitle,
        content: safeContent,
        imageURL,
        videoURL,
        thumbnailURL: thumb?.thumbnailURL || null,
        mediaPath,
        audience,
        bgMusic: bgMusic ? { id: bgMusic.id, title: bgMusic.title, artistId: bgMusic.artistId, artistName: bgMusic.artistName, audioURL: (bgMusic as any).audioURL } : null,
        moderation: {
          status: mediaFile ? 'pending' : 'approved',
          reason: null,
          scores: {},
        },
        hashtags,
        mentions,
        moderationTokens: moderationTokens || null,
        userName,
        userPhoto,
      });
      notifyIfHeldForReview((created as any)?.testimony);

      // Notify mentioned users (best-effort)
      try {
        for (const uname of filteredMentions) {
          const res = await getUserByUsername(uname).catch(() => null);
          const mentionedUserId = res?.profile?.uid;
          if (mentionedUserId && mentionedUserId !== auth.currentUser!.uid) {
            await createNotification(
              mentionedUserId,
              'mention',
              auth.currentUser!.uid,
              auth.currentUser!.displayName || 'Someone',
              (await getMyPhoto()),
              `${auth.currentUser!.displayName || 'Someone'} mentioned you in a testimony`,
              created.id,
              'testimony'
            );
          }
        }
      } catch (e) {
        console.warn('Failed sending mention notifications', e);
      }

      setTitle('');
      setContent('');
  setAudience('public');
      setMediaFile(null);
      setThumbFile(null);
      setUploadProgress(0);
      setShowCreateModal(false);
      setBgMusic(null);
      setEditorCaption('');
      setSuggestions({ open: false, kind: null, query: '', start: -1 });
    } catch (error) {
      console.error('Testimony creation failed:', error);
      const friendly = describeModerationError(error);
      alert(friendly || 'Failed to share testimony');
    } finally {
      setIsUploading(false);
      setPosting(false);
    }
  };

  const handleLikeTestimony = async (testimony: Testimony) => {
    if (!auth.currentUser) return;
    const isLiked = !!testimony.likedByMe;

    try {
      if (isLiked) {
        await unlikeTestimony(testimony.id);
      } else {
        await likeTestimony(testimony.id);
      }
      setTestimonies((prev) => prev.map((t) => {
        if (t.id !== testimony.id) return t;
        const nextLiked = !isLiked;
        const nextCount = Math.max(0, (t.likeCount || 0) + (nextLiked ? 1 : -1));
        return { ...t, likedByMe: nextLiked, likeCount: nextCount } as Testimony;
      }));
      trackInteraction(testimony.id, 'like');
    } catch (error) {
      console.error('Like failed:', error);
    }
  };

  const handleDeleteTestimony = async (t: Testimony) => {
    if (!auth.currentUser || t.userId !== auth.currentUser.uid) return;
    const confirmDel = window.confirm('Delete this testimony? This cannot be undone.');
    if (!confirmDel) return;
    try {
      const path = (t as any).mediaPath as string | undefined;
      if (path) {
        if (String(path).startsWith('media/')) {
          try { await deleteMediaObject(String(path)); } catch {}
        }
      }
      await deleteTestimony(t.id);
      setTestimonies((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) {
      console.error('Delete testimony failed', e);
      alert('Failed to delete');
    }
  };

  const toggleSaveTestimony = async (testimonyId: string) => {
    if (!auth.currentUser) return;
    try {
      // Optimistic UI
      const isSaved = !!savedIds[testimonyId];
      setSavedIds((prev) => {
        const next = { ...prev };
        if (isSaved) delete next[testimonyId];
        else next[testimonyId] = true;
        return next;
      });

      if (isSaved) {
        await unsaveItem('testimony', testimonyId);
      } else {
        await saveItem({ contentType: 'testimony', contentId: testimonyId });
      }
    } catch (e) {
      console.error('Toggle save testimony failed', e);
      // Revert on failure
      setSavedIds((prev) => {
        const next = { ...prev };
        if (next[testimonyId]) delete next[testimonyId];
        else next[testimonyId] = true;
        return next;
      });
    }
  };

  const handleMediaSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    let file = e.target.files[0];
    if (isVideoFile(file)) {
      setMediaFile(file);
      try {
        const duration = await getVideoDuration(file);
        if (duration > MAX_TESTIMONY_SECONDS + 0.5) {
          // Route to full editor instead of quick trim
          setShowEditor(true);
        } else {
          // Keep Quick Share available; editor optional
          setShowEditor(false);
        }
      } catch (err) {
        console.warn('Failed to read video duration', err);
        setShowEditor(false);
      }
      return;
    }
    // Image: optimistic preview then compress
    setMediaFile(file);
    try {
      const compressed = await compressImage(file, { maxWidth: Infinity, maxHeight: Infinity, quality: 0.86, mimeType: 'image/webp' });
      if (compressed !== file) {
        console.log('Compressed testimony image from', file.size, 'to', compressed.size);
        setMediaFile(compressed);
      }
    } catch (err) {
      console.warn('Image compression failed', err);
    }
  };

  // Camera recorder removed; rely on input capture

  const handleRemoveSelectedMedia = () => {
    setMediaFile(null);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const deepId = searchParams.get('id');

  // Filter testimonies based on active tab or deep-link
  // For You: approved testimonies (image or video) from all users
  // Your testimonies: your own testimonies (image or video)
  // If deepId present, show only that testimony
  const displayedTestimonies = deepId
    ? testimonies.filter(t => t.id === deepId)
    : (activeTab === 'forYou'
      ? testimonies.filter(t => !!t.videoURL || !!t.imageURL)
      : testimonies.filter(t => t.userId === auth.currentUser?.uid && (!!t.videoURL || !!t.imageURL)));

  return (
    <div style={styles.container}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
      <div style={{ ...styles.contentArea, ...(isMobile ? styles.contentAreaMobile : {}) }}>
      <header style={{ ...styles.pageHeader, ...(isMobile ? styles.pageHeaderMobile : {}) }}>
        <h1 style={styles.logo}>Testimony Wall</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          style={styles.createButton}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 14px rgba(102,234,209,0.25)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        >
          + Share
        </button>
      </header>

      <div style={styles.subtitle}>
        <p style={styles.subtitleText}>Share your faith journey and inspire others 🙏</p>
      </div>

      {/* Tab Navigation */}
      <div style={{ ...styles.tabContainer, ...(isMobile ? styles.tabContainerMobile : {}) }}>
        <button
          onClick={() => setActiveTab('forYou')}
          style={{
            ...styles.tab,
            ...(isMobile ? styles.tabMobile : {}),
            ...(activeTab === 'forYou' ? styles.activeTab : {})
          }}
          onMouseEnter={(e) => { if (activeTab !== 'forYou') e.currentTarget.style.backgroundColor = '#E8F5F5'; }}
          onMouseLeave={(e) => { if (activeTab !== 'forYou') e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          For You
        </button>
        <button
          onClick={() => setActiveTab('yourTestimonies')}
          style={{
            ...styles.tab,
            ...(isMobile ? styles.tabMobile : {}),
            ...(activeTab === 'yourTestimonies' ? styles.activeTab : {})
          }}
          onMouseEnter={(e) => { if (activeTab !== 'yourTestimonies') e.currentTarget.style.backgroundColor = '#E8F5F5'; }}
          onMouseLeave={(e) => { if (activeTab !== 'yourTestimonies') e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          Your testimonies
        </button>
      </div>

      <main style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
        {displayedTestimonies.length === 0 && (
          <p style={styles.emptyText}>
            {activeTab === 'forYou'
              ? 'No testimonies yet. Be the first to share!'
              : 'You haven\'t shared any testimonies yet.'}
          </p>
        )}
        {displayedTestimonies.length > 0 && (
          displayedTestimonies.map((testimony, tIdx) => (
            <React.Fragment key={testimony.id}>
            {tIdx > 0 && tIdx % 6 === 0 && <div style={{ marginBottom: 16 }}><AdSlot placement="feed" /></div>}
            <div key={testimony.id} style={{...styles.testimonyCard, ...(testimony.featured ? styles.featuredCard : {})}}>
              {/* Track view on mount */}
              <TestimonyViewTracker id={testimony.id} />
              {testimony.featured && <div style={styles.featuredBadge}>⭐ Featured</div>}
              
              <div style={styles.testimonyHeader}>
                <ImageWithFallback src={testimony.userPhoto} alt="" style={styles.userAvatar} />
                <div>
                  <UserLink userId={testimony.userId} as="strong" style={styles.userName}>{testimony.userName}</UserLink>
                  <p style={styles.postTime}>{testimony.createdAt ? new Date(testimony.createdAt).toLocaleDateString() : 'Recently'}</p>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={styles.audienceBadge}>
                      {(((testimony as any).audience || 'public') === 'public') ? '🌍 Public' : '👥 Friends'}
                    </span>
                  </div>
                </div>
              </div>

              <h3 style={styles.testimonyTitle}>{renderTextWithLinks(testimony.title)}</h3>
              <p style={styles.testimonyContent}>{renderTextWithLinks(testimony.content)}</p>

              {testimony.imageURL && (
                <div style={{ position: 'relative' }} ref={(el) => { testimonyContainerRefs.current[testimony.id] = el; if (el) el.dataset.testimonyid = testimony.id; }}>
                  <MediaBox src={testimony.imageURL} type="image" alt="Testimony image" />
                  {(testimony as any).bgMusic?.audioURL && (
                    <div style={{ marginTop: 0 }}>
                      {/* Hidden auto-playing background audio for testimony images */}
                      <SignedAudio
                        autoPlay
                        loop
                        playsInline
                        controls={false}
                        preload="none"
                        src={(testimony as any).bgMusic.audioURL}
                        muted={!!bgMuted[testimony.id]}
                        ref={(el) => { if (el) { bgAudioRefs.current[testimony.id] = el; el.muted = !!bgMuted[testimony.id]; } }}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}
                  {(testimony as any).bgMusic?.audioURL && (
                    <button
                      aria-label={bgMuted[testimony.id] ? 'Unmute background music' : 'Mute background music'}
                      onClick={() => toggleBgMute(testimony.id)}
                      style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 20, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
                    >
                      {bgMuted[testimony.id] ? '🔇' : '🔊'}
                    </button>
                  )}
                </div>
              )}
              {testimony.videoURL && (
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 'min(100%, 380px)',
                    margin: '0 auto',
                    background: '#072B2C',
                    borderRadius: 16,
                    overflow: 'hidden',
                    aspectRatio: '9 / 16',
                    maxHeight: '72vh',
                    boxShadow: '0 10px 30px rgba(7,43,44,0.18)',
                  }}
               >
                  <AmbientBackdrop />
                  <WatchTracker key={testimony.id} contentType="testimony" contentId={testimony.id} />
                  <SignedVideo
                    src={testimony.videoURL}
                    poster={testimony.thumbnailURL || undefined}
                    controls
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      const r = v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : 0;
                      const tall = r > 0.5 && r < 0.62;
                      setVideoTall((prev) => ({ ...prev, [testimony.id]: tall }));
                    }}
                    onPlay={async () => {
                      try {
                        if (!viewedOnceRef.current.has(testimony.id)) {
                          viewedOnceRef.current.add(testimony.id);
                          await viewTestimony(testimony.id);
                          setTestimonies((prev) => prev.map((t) => t.id === testimony.id ? { ...t, views: (t.views || 0) + 1 } : t));
                        }
                      } catch {}
                    }}
                    style={{ position: 'relative', width: '100%', height: '100%', objectFit: videoTall[testimony.id] ? 'cover' : 'contain', display: 'block' }}
                  />
                </div>
              )}

              <div style={{ ...styles.testimonyActions, ...(isMobile ? styles.testimonyActionsMobile : {}) }}>
                {auth.currentUser?.uid === testimony.userId && (
                  <>
                    <button
                      onClick={() => {
                        setEditingTestimony(testimony);
                        setEditTitle(testimony.title || '');
                        setEditContent(testimony.content || '');
                        setEditAudience((((testimony as any).audience || 'public') as 'public' | 'friends'));
                      }}
                      style={styles.actionButton}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTestimony(testimony)}
                      style={styles.actionButton}
                      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                    >
                      🗑️ Delete
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleLikeTestimony(testimony)}
                  style={styles.actionButton}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                >
                  {testimony.likedByMe ? '❤️' : '🤍'} {testimony.likeCount || 0}
                </button>
                <button
                  onClick={() => { trackInteraction(testimony.id, 'comment'); setCommentingTestimony(testimony); }}
                  style={styles.actionButton}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                >
                  💬 {testimony.comments}
                </button>
                <button
                  onClick={() => { setRepostTarget(testimony); setRepostQuote(''); setShowRepostModal(true); }}
                  style={styles.actionButton}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                >
                  🔁 Repost
                </button>
                <button
                  onClick={() => toggleSaveTestimony(testimony.id)}
                  style={styles.actionButton}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                >
                  {savedIds[testimony.id] ? '✅ Saved' : '📌 Save'}
                </button>
                <button
                  onClick={async () => {
                    try {
                      const shareUrl = `${window.location.origin}/testimony?id=${encodeURIComponent(testimony.id)}`;
                      const shareData: ShareData = {
                        title: testimony.title || 'Testimony on Belivah',
                        text: testimony.content || 'Check out this testimony on Belivah',
                        url: shareUrl,
                      };
                      if ((navigator as any).share) {
                        await (navigator as any).share(shareData);
                      } else {
                        await navigator.clipboard.writeText(shareUrl);
                        alert('Link copied to clipboard');
                      }
                      trackInteraction(testimony.id, 'share');
                    } catch (e) {
                      console.warn('Share failed', e);
                    }
                  }}
                  style={styles.actionButton}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.transform = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                >
                  🔗 Share
                </button>
                <button
                  style={styles.actionButton}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#0B6B6F'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#5B6E6E'; e.currentTarget.style.transform = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-0.5px)'; }}
                >
                  👁️ {testimony.views || 0}
                </button>
              </div>
            </div>
            </React.Fragment>
          ))
        )}
      </main>

      {showCreateModal && (
        <div style={styles.overlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Share Your Testimony</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                style={styles.closeButton}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#0B6B6F'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#5B6E6E'; }}
                onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.95)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = 'none'; }}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <label htmlFor="testimony-audience" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Audience</label>
                <select
                  id="testimony-audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value as 'public' | 'friends')}
                  style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
                >
                  <option value="public">Public</option>
                  <option value="friends">Friends</option>
                </select>
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (e.g., 'How God Changed My Life')"
                style={styles.titleInput}
              />
              <textarea
                value={content}
                onChange={(e) => {
                  const val = e.target.value;
                  setContent(val);
                  // Simple inline suggestions logic similar to Home composer
                  const el = contentRef.current;
                  const caret = el ? el.selectionStart || val.length : val.length;
                  const upto = val.slice(0, caret);
                  const tokenMatch = upto.match(/(^|\s)([@#][a-zA-Z0-9_]*)$/);
                  if (tokenMatch) {
                    const token = tokenMatch[2] || '';
                    const kind = token.startsWith('@') ? 'mention' : 'hashtag';
                    const queryTxt = token.slice(1).toLowerCase();
                    setSuggestions({ open: queryTxt.length >= 1, kind, query: queryTxt, start: caret - token.length });
                  } else {
                    setSuggestions({ open: false, kind: null, query: '', start: -1 });
                  }
                }}
                placeholder="Share your testimony..."
                style={styles.contentTextarea}
                rows={8}
                ref={contentRef}
              />
              {suggestions.open && (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', zIndex: 20, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, marginTop: 6, padding: 6, maxHeight: 180, overflowY: 'auto', width: '100%' }}>
                    {suggestions.kind === 'mention' ? (
                      <>
                        {/* Pinned @followers option */}
                        {'followers'.includes(suggestions.query) && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              const before = content.slice(0, suggestions.start);
                              const after = content.slice(suggestions.start + suggestions.query.length + 1);
                              setContent(`${before}@followers${after}`);
                              setSuggestions({ open: false, kind: null, query: '', start: -1 });
                            }}
                            style={{ padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <span style={{ fontWeight: 700 }}>@followers</span>
                            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Notify all your followers</span>
                          </div>
                        )}
                        {mentionCandidates
                          .filter((u) => (u.username || '').toLowerCase().includes(suggestions.query) || (u.name || '').toLowerCase().includes(suggestions.query))
                          .slice(0, 6)
                          .map((u) => (
                            <div
                              key={u.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                const before = content.slice(0, suggestions.start);
                                const after = content.slice(suggestions.start + suggestions.query.length + 1);
                                const insert = `@${(u.username || u.name || '').replace(/\s+/g, '')}`;
                                setContent(`${before}${insert}${after}`);
                                setSuggestions({ open: false, kind: null, query: '', start: -1 });
                              }}
                              style={{ padding: '6px 8px', cursor: 'pointer' }}
                            >
                              @{u.username || u.name}
                            </div>
                          ))}
                        {mentionCandidates.filter((u) => (u.username || '').toLowerCase().includes(suggestions.query) || (u.name || '').toLowerCase().includes(suggestions.query)).length === 0 && (
                          <div style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>No matches</div>
                        )}
                      </>
                    ) : (
                      <>
                        {Array.from(new Set([
                          // Gather tags from existing testimonies
                          ...testimonies.flatMap((t) => extractHashtags(`${t.title || ''} ${t.content || ''}`))
                        ]))
                          .filter((t) => String(t).toLowerCase().includes(suggestions.query))
                          .slice(0, 8)
                          .map((t) => (
                            <div
                              key={t}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                const before = content.slice(0, suggestions.start);
                                const after = content.slice(suggestions.start + suggestions.query.length + 1);
                                setContent(`${before}#${t}${after}`);
                                setSuggestions({ open: false, kind: null, query: '', start: -1 });
                              }}
                              style={{ padding: '6px 8px', cursor: 'pointer' }}
                            >
                              #{t}
                            </div>
                          ))}
                      </>
                    )}
                  </div>
                </div>
              )}
              
              <div style={styles.mediaSection}>
                <label htmlFor="testimony-media" style={styles.mediaLabel}>
                  {isUploading
                    ? `⬆️ Uploading... ${Math.round(uploadProgress)}%`
                    : mediaFile 
                    ? `📎 ${mediaFile.name}` 
                    : '📷 Add Photo/Video (required)'}
                </label>
                <input
                  id="testimony-media"
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaSelect}
                  style={styles.fileInput}
                  disabled={false}
                  ref={mediaInputRef}
                />
                {mediaFile && (
                  <div style={{ marginTop: 8 }}>
                    <button type="button" style={styles.smallButtonDanger} onClick={handleRemoveSelectedMedia}>Remove Selected</button>
                    {isVideoFile(mediaFile) && <ThumbnailPicker file={thumbFile} onChange={setThumbFile} disabled={isUploading} videoFile={mediaFile} />}
                  </div>
                )}
                {/* When a video is selected, offer Quick Share or open the editor */}
                {mediaFile && isVideoFile(mediaFile) && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setShowEditor(true)}
                      style={styles.submitButton}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(11,107,111,0.25)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    >
                      Open Editor / Trim
                    </button>
                    <button
                      onClick={handleCreateTestimony}
                      disabled={posting}
                      style={styles.smallButtonSecondary}
                    >
                      {posting ? 'Sharing…' : 'Quick Share'}
                    </button>
                  </div>
                )}
              </div>

              {/* Background music selection for image testimonies */}
              {mediaFile && mediaFile.type.startsWith('image/') && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  {bgMusic ? (
                    <>
                      <span style={{ fontWeight: 600 }}>🎵 {bgMusic.title}</span>
                      <button type="button" style={styles.smallButtonSecondary} onClick={() => setShowPickMusic(true)}>Change</button>
                      <button type="button" style={styles.smallButtonDanger} onClick={() => setBgMusic(null)}>Clear</button>
                    </>
                  ) : (
                    <button type="button" style={styles.smallButtonSecondary} onClick={() => setShowPickMusic(true)}>Add Music</button>
                  )}
                </div>
              )}

              {/* For images (or when no media yet), keep the main submit button */}
              {(!mediaFile || (mediaFile && !isVideoFile(mediaFile))) && (
                <button
                  onClick={handleCreateTestimony}
                  disabled={posting || !mediaFile}
                  style={styles.submitButton}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(11,107,111,0.25)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                >
                  {posting ? 'Sharing...' : 'Share Testimony'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Music picker for image testimonies */}
      {showPickMusic && (
        <MusicLibraryPicker
          open={showPickMusic}
          onClose={() => setShowPickMusic(false)}
          onSelect={(track) => { setBgMusic(track); setShowPickMusic(false); }}
        />
      )}

      {/* Camera recording modal removed */}

      {editingTestimony && (
        <div style={styles.overlay} onClick={() => setEditingTestimony(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Edit Testimony</h3>
              <button onClick={() => setEditingTestimony(null)} style={styles.closeButton}>✕</button>
            </div>
            <div style={styles.modalContent}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <label htmlFor="edit-testimony-audience" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Audience</label>
                <select
                  id="edit-testimony-audience"
                  value={editAudience}
                  onChange={(e) => setEditAudience(e.target.value as 'public' | 'friends')}
                  style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
                >
                  <option value="public">Public</option>
                  <option value="friends">Friends</option>
                </select>
              </div>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Title"
                style={styles.titleInput}
              />
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Update your testimony..."
                style={styles.contentTextarea}
                rows={6}
              />
              {editingTestimony?.videoURL && <ThumbnailPicker file={editThumb} onChange={setEditThumb} />}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditingTestimony(null)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={async () => {
                    if (!editingTestimony) return;
                    try {
                      const thumb = editThumb && editingTestimony.videoURL ? await uploadThumbnail(editThumb) : null;
                      await updateTestimony(editingTestimony.id, {
                        title: filterProfanity(editTitle || ''),
                        content: filterProfanity(editContent || ''),
                        audience: editAudience,
                        ...(thumb ? { thumbnailURL: thumb.thumbnailURL, moderationTokens: { thumbnailURL: thumb.proof } } : {}),
                      });
                      setTestimonies((prev) => prev.map((t) => t.id === editingTestimony.id ? { ...t, title: editTitle, content: editContent, ...(thumb ? { thumbnailURL: thumb.thumbnailURL } : {}) } : t));
                      setEditThumb(null);
                      setEditingTestimony(null);
                    } catch (e) {
                      console.error('Edit testimony failed', e);
                      alert('Failed to save changes');
                    }
                  }}
                  style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0B6B6F', color: '#fff', cursor: 'pointer' }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {showRepostModal && (
          <div style={styles.overlay} onClick={() => setShowRepostModal(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Repost Testimony</h3>
                <button
                  onClick={() => setShowRepostModal(false)}
                  style={styles.closeButton}
                >
                  ✕
                </button>
              </div>
              <div style={styles.modalContent}>
                <p style={{ marginTop: 0, marginBottom: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>Optional: add a comment</p>
                <textarea
                  value={repostQuote}
                  onChange={(e) => setRepostQuote(e.target.value)}
                  rows={3}
                  placeholder="Say something about this... (optional)"
                  style={{ width: '100%', border: '2px solid #17A2A9', borderRadius: 8, padding: 10, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
                  <button onClick={() => setShowRepostModal(false)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                  <button
                    onClick={async () => {
                      if (!auth.currentUser || !repostTarget || postingRepost) return;
                      setPostingRepost(true);
                      try {
                        const original: any = {
                          id: repostTarget.id,
                          userId: repostTarget.userId,
                          userName: repostTarget.userName,
                          userPhoto: repostTarget.userPhoto || '',
                          content: (repostTarget.content || repostTarget.title || ''),
                          imageURL: repostTarget.imageURL || undefined,
                          videoURL: repostTarget.videoURL || undefined,
                          createdAt: (repostTarget as any).createdAt || new Date(),
                        };
                        await createPost({
                          content: repostQuote.trim(),
                          type: 'repost',
                          repostOf: repostTarget.id,
                          original,
                          moderation: { status: 'approved', reason: null, scores: {} },
                          userName: auth.currentUser.displayName || 'Anonymous',
                          userPhoto: (await getMyPhoto()),
                        });
                        setShowRepostModal(false);
                        setRepostTarget(null);
                        setRepostQuote('');
                        alert('Reposted to your timeline');
                      } catch (e) {
                        console.error('Repost failed', e);
                        alert('Failed to repost');
                      } finally {
                        setPostingRepost(false);
                      }
                    }}
                    disabled={postingRepost}
                    style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0B6B6F', color: '#fff', cursor: 'pointer' }}
                  >
                    {postingRepost ? 'Reposting…' : 'Repost'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {commentingTestimony && (
          <CommentSection
            contentId={commentingTestimony.id}
            contentType="testimony"
            contentOwnerId={commentingTestimony.userId}
            onClose={() => setCommentingTestimony(null)}
          />
        )}
        {/* Video editor and quick trim for testimonies */}
        {showEditor && mediaFile && isVideoFile(mediaFile) && (
          <ReelEditorV2
            open={showEditor}
            videoFile={mediaFile}
            onClose={() => setShowEditor(false)}
            onSave={(blob) => {
              const edited = new File([blob], `${Date.now()}_testimony.webm`, { type: blob.type || 'video/webm' });
              setMediaFile(edited);
              setShowEditor(false);
            }}
            maxDurationSeconds={MAX_TESTIMONY_SECONDS}
            onBgMusicChange={(track) => setBgMusic(track)}
            initialCaption={editorCaption}
            onCaptionChange={(c) => setEditorCaption(c)}
          />
        )}
        {/* Quick trim removed */}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-alt)',
  },
  contentArea: {
    paddingTop: '76px',
    marginLeft: '300px',
    marginRight: '340px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  contentAreaMobile: {
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingTop: '12px',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 30px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  pageHeaderMobile: {
    padding: '10px 14px',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  createButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary-300)',
    color: 'var(--color-text)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  subtitle: {
    backgroundColor: 'var(--color-primary-300)',
    padding: '12px',
    textAlign: 'center',
  },
  subtitleText: {
    margin: 0,
    fontSize: '16px',
    color: 'var(--color-text)',
    fontWeight: '500',
  },
  main: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '16px',
    paddingBottom: '80px',
  },
  mainMobile: {
    padding: '12px',
    paddingBottom: 88,
  },
  emptyText: {
    textAlign: 'center',
    color: '#5B6E6E',
    fontSize: '16px',
    padding: '40px',
  },
  testimonyCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
  },
  featuredCard: {
    border: '3px solid #FFD700',
    boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
  },
  featuredBadge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: '#FFD700',
    color: '#072B2C',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  testimonyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '15px',
  },
  userAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  userName: { fontSize: '16px', color: 'var(--color-text)' },
  postTime: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
  audienceBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid var(--color-border)',
    color: 'var(--color-text-muted)',
    fontSize: 12,
    background: 'var(--color-surface)',
  },
  testimonyTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: 'var(--color-primary)',
    marginBottom: '12px',
  },
  testimonyContent: {
    fontSize: '16px',
    color: 'var(--color-text)',
    lineHeight: '1.6',
    marginBottom: '15px',
    whiteSpace: 'pre-wrap',
  },
  // testimonyMedia removed: handled by MediaBox component
  testimonyActions: {
    display: 'flex',
    gap: '20px',
    paddingTop: '15px',
    borderTop: '1px solid #E0E0E0',
  },
  testimonyActionsMobile: {
    flexWrap: 'wrap',
    gap: '12px',
    rowGap: '10px',
  },
  actionButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--color-text-muted)',
    transition: 'color 0.2s, transform 0.2s',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '85vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #E0E0E0',
  },
  modalTitle: { margin: 0, fontSize: '20px', fontWeight: 'bold', color: 'var(--color-text)' },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    padding: '0',
    transition: 'color 0.2s, transform 0.2s',
  },
  modalContent: {
    padding: '20px',
  },
  titleInput: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
    marginBottom: '15px',
  },
  contentTextarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '15px',
  },
  mediaSection: {
    marginBottom: '20px',
  },
  mediaLabel: {
    display: 'inline-block',
    padding: '10px 20px',
    backgroundColor: 'var(--color-background-alt)',
    color: 'var(--color-text)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  fileInput: {
    display: 'none',
  },
  smallButtonSecondary: {
    padding: '6px 10px',
    backgroundColor: 'var(--color-background-alt)',
    color: 'var(--color-text)',
    border: '1px solid #E0E0E0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  smallButtonDanger: {
    padding: '6px 10px',
    backgroundColor: '#b71c1c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  submitButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    padding: '20px',
    backgroundColor: 'var(--color-background-alt)',
    borderBottom: '2px solid #E0E0E0',
  },
  tabContainerMobile: {
    flexWrap: 'wrap',
    padding: '12px 10px',
    gap: '8px',
  },
  tab: {
    padding: '10px 30px',
    fontSize: '16px',
    fontWeight: '500',
    color: 'var(--color-text-muted)',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s',
  },
  tabMobile: {
    flex: '1 1 calc(50% - 8px)',
    textAlign: 'center',
  },
  activeTab: {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
  },
};

export default TestimonyWall;
